import { Component, EventEmitter, Input, Output, OnInit, signal, computed, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';

import { AuthService } from '../../../core/services/auth.service';
import { ChargerNotificationsService } from '../../../core/services/charger-notifications.service';

/**
 * Barra superior: título de la sección actual (según la ruta), contador de
 * notificaciones de cargadores no leídas y botón para mostrar/ocultar el sidebar.
 */
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule, AvatarModule, ButtonModule, ConfirmDialogModule, MenuModule, OverlayBadgeModule],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TopbarComponent implements OnInit {
  @Input() sidebarVisible = true;
  @Output() toggleSidebar = new EventEmitter<void>();

  currentUser = signal<any>(null);

  userInitial = computed(() => {
    const name = this.currentUser()?.name;
    return name ? name.charAt(0).toUpperCase() : 'U';
  });

  currentTitle = signal('Dashboard');
  userMenuItems: MenuItem[] = [];

  unreadCount = signal<number>(0);
  hasUnreadNotification = computed(() => {
    return this.unreadCount() > 0;
  });

  // Mapa de rutas a títulos para fácil mantenimiento
  private readonly ROUTE_TITLES: Record<string, string> = {
    // Catálogos
    'roles': 'Perfiles',
    'regions': 'Regiones',
    'commercial-zones': 'Zonas Comerciales',
    'zones': 'Zonas',
    'users': 'Usuarios',
    'commercial-conditions': 'Condiciones Comerciales',
    'customer-groups': 'Grupos de Clientes',
    'connector-types': 'Tipos de Conectores',
    'power-ranges': 'Niveles de carga',
    'postal-codes': 'Códigos Postales',
    'vehicle-models': 'Modelos de Vehículos',
    'companies': 'Empresas',
    'customers': 'Clientes',
    'faqs': 'Preguntas Frecuentes',
    'amenities': 'Amenidades',
    'stations': 'Estaciones',
    'chargers': 'Cargadores',
    'connectors': 'Conectores',
    // Gestión
    'stations-map': 'Mapa de Estaciones',
    'charging-transactions': 'Historial de Cargas',
    'station-reviews': 'Reseñas',
    'push-notifications': 'Notificaciones',
    'reservation-policies': 'Políticas de Reserva',
    'loyalty-programs': 'Programas de Lealtad',
    'yena-products': 'Productos Yena',
    'invoices': 'Facturas',
    'charger-notifications': 'Notificaciones de Cargadores',
    'price-configuration': 'Configuración de Precios',
    'missing-price-configurations-report': 'Reporte de Precios Faltantes'
  };

  constructor(
    private authService: AuthService,
    private chargerNotificationsService: ChargerNotificationsService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.updateTitle());

    this.chargerNotificationsService.unreadCount$.subscribe((count) => {
      this.unreadCount.set(count);
    });
  }

  ngOnInit() {
    this.currentUser.set(this.authService.currentUser());
    this.chargerNotificationsService.loadUnreadCount();
    this.updateMenu();
    this.updateTitle();
  }

  updateMenu() {
    this.userMenuItems = [
      {
        label: 'Cerrar sesión',
        icon: 'pi pi-sign-out',
        styleClass: 'item-cerrar-sesion',
        command: () => this.confirmLogout()
      }
    ];
  }

  private confirmLogout(): void {
    this.confirmationService.confirm({
      header: 'Confirmar cierre de sesión',
      message: '¿Deseas cerrar sesión?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, cerrar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.authService.logout()
    });
  }

  private updateTitle() {
    // Dividimos la URL en partes: urlTree parsea la URL y quita query params si los hubiera
    const urlTree = this.router.parseUrl(this.router.url);
    const segment = urlTree.root.children['primary']?.segments[0]?.path;

    // Si es la ruta raíz
    if (this.router.url === '/') {
      this.currentTitle.set('Dashboard');
      return;
    }

    // Buscar título en el mapa, o usar 'Go Energy' como default
    this.currentTitle.set(this.ROUTE_TITLES[segment || ''] || 'Go Energy');
  }
}
